// One fetch path for the whole dashboard.
//
// Two things it exists to stop, both of which the hand-written page got wrong
// at least once:
//
//   1. A result painting into a view that has moved on. Every request carries a
//      token; a response holding a stale one is dropped rather than rendered.
//      The old page did this per-modal with a module-level counter, which meant
//      every new modal had to remember to do it.
//   2. A failure rendering as an empty panel. docs/GUARDRAILS.md §5: a failure
//      is reported, never silently dropped. `error` is part of the shape, and
//      every consumer has to deal with it to render anything.

import { ref, shallowRef, watch, onScopeDispose, toValue } from 'vue'
import { lang, t } from '../lib/i18n.js'
import { scope } from '../lib/scope.js'

/**
 * Build `/api/...?a=b`, dropping params that are null/undefined/empty.
 *
 * In Japanese it adds `lang=ja`, so the labels the Worker writes (a window's
 * date, an axis's buckets) arrive in Japanese, and an answer in one language
 * is never reused for the other. English adds nothing (decision 0030).
 */
export function apiUrl(path, params = {}) {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue
    qs.set(key, Array.isArray(value) ? value.join(',') : String(value))
  }
  if (lang.value === 'ja') qs.set('lang', 'ja')
  // Diagnostics' Hotel site view reads the hotel-site rows (lib/scope.js).
  if (scope()) qs.set('scope', scope())
  const query = qs.toString()
  return `/api/${path}${query ? `?${query}` : ''}`
}

/**
 * Fetch JSON once.
 *
 * Throws with the server's own message when it sent one. The Worker answers
 * errors as `{ error: "..." }`, and "the request did not complete" is a worse
 * thing to show a reader than whatever the Worker actually said.
 */
export async function fetchJson(url, { signal, method = 'GET', body: payload } = {}) {
  const headers = { accept: 'application/json' }
  // A body is always JSON here: the only POST that takes one is the Slack
  // hand-off, and the Worker reads it with `request.json()`.
  if (payload !== undefined) headers['content-type'] = 'application/json'
  const res = await fetch(url, {
    signal,
    method,
    headers,
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })
  // Declared without an initialiser: every path out of the catch throws, so a
  // `= null` here is a value nothing can ever read.
  let body
  try {
    body = await res.json()
  } catch {
    // A non-JSON body on a failing response is the Worker falling over, or
    // Cloudflare Access returning its sign-in page to a fetch that expected
    // data. Both are worth naming rather than showing as a parse error.
    if (!res.ok) throw new Error(`${res.status} ${res.statusText || 'request failed'}`)
    throw new Error(t('the server sent something that was not JSON'))
  }
  // `/api/handoff` explains a refusal as `{ sent: false, reason }` rather than
  // `{ error }`, and the reason ("Slack said: channel_not_found") is the one
  // thing the reader needs. Read either.
  if (!res.ok) throw new Error(body?.error ?? body?.reason ?? `${res.status} ${res.statusText}`)
  if (body?.error) throw new Error(body.error)
  return body
}

/**
 * One answer per question, shared by every component that asks it.
 *
 * For rows that cannot change under the reader: a past day's never do, and
 * today's change once, at the 07:00 run. So an answer is kept for a few minutes
 * and a request still on its way is shared rather than sent twice. `fresh` (a
 * retry) always asks the server. Nothing reading a shared answer may change it
 * in place — that is what makes sharing it safe.
 */
const KEEP_MS = 5 * 60 * 1000
const answers = new Map()

export function sharedAnswer(url, { fresh = false } = {}) {
  const hit = answers.get(url)
  if (!fresh && hit && Date.now() - hit.at < KEEP_MS) return hit.body
  // Not tied to the asking component's signal: another may be waiting on it.
  const body = fetchJson(url).catch((err) => {
    answers.delete(url)
    throw err
  })
  answers.set(url, { at: Date.now(), body })
  return body
}

/** Forget every kept answer. The test setup calls it before each test. */
export const forgetAnswers = () => answers.clear()

/**
 * A reactive GET that re-runs when its inputs change and never paints a stale
 * answer.
 *
 * `source` is a getter returning `[path, params]`, or null to not fetch at all —
 * which is how a modal says "I have no subject yet" without a second flag.
 * `fetcher` replaces `fetchJson`; it gets `{ signal, fresh }`, and `fresh` is
 * true on `refresh()`, which must always ask the server again.
 */
export function useFetch(source, { immediate = true, fetcher = fetchJson } = {}) {
  const data = shallowRef(null)
  const error = ref(null)
  const pending = ref(false)
  /** True only for the very first load, so a reload can keep the old answer on
   *  screen and fade it rather than replacing it with grey blocks. */
  const first = ref(true)

  let token = 0
  let controller = null

  async function run(fresh = false) {
    const spec = toValue(source)
    if (!spec) {
      data.value = null
      error.value = null
      pending.value = false
      return
    }
    const [path, params] = spec
    const mine = ++token
    controller?.abort()
    controller = new AbortController()
    pending.value = true
    error.value = null
    try {
      const body = await fetcher(apiUrl(path, params), { signal: controller.signal, fresh })
      if (mine !== token) return
      data.value = body
    } catch (err) {
      if (mine !== token || err?.name === 'AbortError') return
      error.value = err instanceof Error ? err : new Error(String(err))
      // The previous answer is deliberately left on screen beneath the error
      // banner. Blanking it as well turns one failed refresh into a page that
      // has lost everything the reader was looking at.
    } finally {
      if (mine === token) {
        pending.value = false
        first.value = false
      }
    }
  }

  if (immediate) watch(source, () => run(), { immediate: true, deep: false })
  onScopeDispose(() => controller?.abort())

  return { data, error, pending, first, refresh: () => run(true) }
}
